type Changes<T> = {
	[K in keyof T]: { from: T[K]; to: unknown } | undefined
}

/**
 * Deeply compares two objects and returns changed and unchanged properties.
 *
 * @param obj1 Source object
 * @param obj2 Target object
 * @param path Current property path (used for recursion)
 * @param deepArray Whether to compare and return indexed changes in arrays. Setting to false will return the entire array if any change is detected.
 * @returns Object containing changes and unchanged properties
 */
export function deepDiff<T extends object, U extends object>({
	source,
	target,
	path = '',
	deepArray = true
}: {
	source: T
	target: U
	path?: string
	/** Whether to compare and return indexed changes in arrays. Setting to false will return the entire array if any change is detected. */
	deepArray?: boolean
}) {
	const changes = {} as Changes<T>
	const unchanged: Partial<T> = {}

	for (const key of new Set([...Object.keys(source), ...Object.keys(target)])) {
		const fullPath = path ? `${path}.${key}` : key
		const val1 = source[key]
		const val2 = target[key]

		if (val1 !== null && val2 !== null && typeof val1 === 'object' && typeof val2 === 'object') {
			if (!deepArray && Array.isArray(val1) && Array.isArray(val2)) {
				if (JSON.stringify(val1) !== JSON.stringify(val2)) {
					changes[fullPath] = { from: val1, to: val2 }
				} else {
					unchanged[fullPath] = val1
				}
			} else {
				const { changes: nestedChanges, unchanged: nestedUnchanged } = deepDiff({
					source: val1,
					target: val2,
					path: fullPath,
					deepArray
				})

				Object.assign(changes, nestedChanges)
				Object.assign(unchanged, nestedUnchanged)
			}
		} else if (val1 !== val2) {
			changes[fullPath] = { from: val1, to: val2 }
		} else {
			unchanged[fullPath] = val1
		}
	}

	return { changes, unchanged } as const
}
